import { useEffect, useRef } from 'react';
import { geoCircle, geoDistance, geoGraticule10, geoInterpolate, geoOrthographic, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Topology } from 'topojson-specification';
import type { FeatureCollection } from 'geojson';
import type { MapPoint } from './WorldMap.tsx';

let land: Promise<FeatureCollection> | null = null;
const loadLand = () =>
  (land ??= import('world-atlas/countries-110m.json').then((m) => {
    const topo = ((m as { default?: unknown }).default ?? m) as Topology;
    return feature(topo, topo.objects.countries!) as unknown as FeatureCollection;
  }));

const COLOR: Record<string, string> = { 'on-air': '#ff3b30', planned: '#ffd21f', ready: '#3ddc84', warm: '#4cc9f0', held: '#6b7280', standby: '#3a4150' };

/** Where the sun is overhead now (approximate: enough to shade the night side). */
function subsolar(now: Date): [number, number] {
  const day = (Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - Date.UTC(now.getUTCFullYear(), 0, 0)) / 86_400_000;
  const decl = -23.44 * Math.cos(((2 * Math.PI) / 365) * (day + 10));
  const hours = now.getUTCHours() + now.getUTCMinutes() / 60;
  return [-(hours - 12) * 15, decl];
}

/**
 * The camera pool on a turning globe: night side shaded, the camera on air pulsing, arcs to the cameras
 * planned next. Turns towards the camera on air; drag to look around (it resumes turning after a while).
 */
export function Globe3D({ points }: { points: MapPoint[] }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const pts = useRef(points);
  useEffect(() => {
    pts.current = points;
  });
  useEffect(() => {
    const cv = canvas.current;
    if (!cv) return;
    let raf = 0;
    let countries: FeatureCollection | null = null;
    void loadLand().then((c) => (countries = c));
    const rot: [number, number] = [-20, -25];
    let dragging: { x: number; y: number; r: [number, number] } | null = null;
    let idleUntil = 0;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const down = (e: PointerEvent) => {
      dragging = { x: e.clientX, y: e.clientY, r: [rot[0], rot[1]] };
      cv.setPointerCapture(e.pointerId);
    };
    const move = (e: PointerEvent) => {
      if (!dragging) return;
      rot[0] = dragging.r[0] + (e.clientX - dragging.x) * 0.35;
      rot[1] = Math.max(-80, Math.min(80, dragging.r[1] - (e.clientY - dragging.y) * 0.35));
      idleUntil = performance.now() + 6000;
    };
    const up = () => (dragging = null);
    cv.addEventListener('pointerdown', down);
    cv.addEventListener('pointermove', move);
    cv.addEventListener('pointerup', up);
    let last = performance.now();
    const draw = (now: number) => {
      raf = requestAnimationFrame(draw);
      const dt = Math.min(100, now - last);
      last = now;
      const dpr = Math.min(2, devicePixelRatio || 1);
      const w = cv.clientWidth;
      const h = cv.clientHeight;
      if (!w || !h) return;
      if (cv.width !== Math.round(w * dpr)) {
        cv.width = Math.round(w * dpr);
        cv.height = Math.round(h * dpr);
      }
      const g = cv.getContext('2d')!;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, w, h);
      const onAir = pts.current.find((p) => p.kind === 'on-air');
      // Turn: towards the camera on air (eased), else slowly around.
      if (!dragging && now > idleUntil && !reduced) {
        if (onAir) {
          const tx = -onAir.lon;
          const ty = -Math.max(-60, Math.min(60, onAir.lat));
          let d = ((tx - rot[0] + 540) % 360) - 180;
          rot[0] += d * Math.min(1, dt / 900);
          d = ty - rot[1];
          rot[1] += d * Math.min(1, dt / 900);
          if (Math.abs(((tx - rot[0] + 540) % 360) - 180) < 0.5) rot[0] += dt * 0.004;
        } else rot[0] += dt * 0.01;
      }
      const r = Math.min(w, h) / 2 - 8;
      const proj = geoOrthographic().scale(r).translate([w / 2, h / 2]).rotate([rot[0], rot[1]]).clipAngle(90);
      const path = geoPath(proj, g);
      // ocean + glow
      const grd = g.createRadialGradient(w / 2 - r * 0.3, h / 2 - r * 0.3, r * 0.1, w / 2, h / 2, r);
      grd.addColorStop(0, '#16202e');
      grd.addColorStop(1, '#0b1018');
      g.beginPath();
      path({ type: 'Sphere' });
      g.fillStyle = grd;
      g.fill();
      g.lineWidth = 1;
      g.strokeStyle = 'rgba(255,255,255,0.05)';
      g.beginPath();
      path(geoGraticule10());
      g.stroke();
      if (countries) {
        g.beginPath();
        path(countries);
        g.fillStyle = '#263041';
        g.fill();
        g.strokeStyle = '#334055';
        g.lineWidth = 0.5;
        g.stroke();
      }
      // night side
      const [sx, sy] = subsolar(new Date());
      g.beginPath();
      path(geoCircle().center([sx + 180, -sy]).radius(90)());
      g.fillStyle = 'rgba(0,0,0,0.38)';
      g.fill();
      // arcs on air → planned
      if (onAir) {
        for (const p of pts.current.filter((x) => x.kind === 'planned')) {
          const ip = geoInterpolate([onAir.lon, onAir.lat], [p.lon, p.lat]);
          const coords = Array.from({ length: 41 }, (_, i) => ip(i / 40));
          g.beginPath();
          path({ type: 'LineString', coordinates: coords });
          g.strokeStyle = 'rgba(255,210,31,0.55)';
          g.lineWidth = 1.5;
          g.setLineDash([4, 4]);
          g.stroke();
          g.setLineDash([]);
        }
      }
      // points (front side only)
      const order = ['standby', 'held', 'ready', 'warm', 'planned', 'on-air'];
      for (const p of [...pts.current].sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind))) {
        // Only the side facing us (angular distance from the view centre below 90°).
        if (geoDistance([p.lon, p.lat], [-rot[0], -rot[1]]) > Math.PI / 2) continue;
        const xy = proj([p.lon, p.lat]);
        if (!xy) continue;
        const rad = p.kind === 'on-air' ? 5.5 : p.kind === 'planned' ? 4.5 : 3;
        if (p.kind === 'on-air') {
          const pulse = (now % 1600) / 1600;
          g.beginPath();
          g.arc(xy[0], xy[1], 6 + pulse * 16, 0, Math.PI * 2);
          g.strokeStyle = `rgba(255,59,48,${1 - pulse})`;
          g.lineWidth = 2;
          g.stroke();
        }
        g.beginPath();
        g.arc(xy[0], xy[1], rad, 0, Math.PI * 2);
        g.fillStyle = COLOR[p.kind] ?? '#888';
        g.fill();
        g.strokeStyle = 'rgba(0,0,0,0.6)';
        g.lineWidth = 1;
        g.stroke();
      }
      // rim light
      g.beginPath();
      path({ type: 'Sphere' });
      g.strokeStyle = 'rgba(120,170,255,0.25)';
      g.lineWidth = 2;
      g.stroke();
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      cv.removeEventListener('pointerdown', down);
      cv.removeEventListener('pointermove', move);
      cv.removeEventListener('pointerup', up);
    };
  }, []);
  return <canvas ref={canvas} className="globe3d" role="img" aria-label={`Camera pool globe: ${points.length} cameras`} />;
}
